import { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { ICampaign } from '../../../../interfaces/models/campaign';
import { ISignalSettingsResponse, SignalsMode } from '../../../../interfaces/models/signals';
import { useSignalsRequest } from '../../../../services/apis/signals/useSignalsRequest';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';
import { useAppActions } from '../../../../stores/application/useAppActions';
import { filterEnrollableCampaigns } from '../../../../utils/campaignContactUtils';
import { Card, CardHead, CardTitle, Muted, Pill, PrimaryButton, Select } from './signalsUi';

interface ISignalSettingsPanelProps {
  data: ISignalSettingsResponse;
  /** After a save — reload settings, and the list (scores and hidden leads change). */
  onSaved: () => void;
}

const FIELDS: { key: 'offer' | 'idealBuyer' | 'notAFit'; label: string; placeholder: string; rows: number }[] = [
  { key: 'offer', label: 'What you sell', placeholder: 'Sales training for software companies.', rows: 2 },
  {
    key: 'idealBuyer',
    label: 'Your ideal buyer',
    placeholder: 'VPs and Heads of Sales at B2B software companies with 50–500 employees, in the US or Europe.',
    rows: 3,
  },
  { key: 'notAFit', label: 'Not a fit', placeholder: 'Students, recruiters, agencies selling to us, competitors.', rows: 2 },
];

/**
 * The company brain and the autopilot switch.
 *
 * Admins describe the ideal buyer in plain words; an LLM scores every new person 0–100
 * against it and says why. Leads under the ignore score are tucked into "Low fit". On
 * autopilot, leads at or above the auto-approve score go straight into their owner's
 * Signals campaign — each member picks their own, since only a campaign's creator can
 * add leads to it.
 */
const SignalSettingsPanel: FunctionComponent<ISignalSettingsPanelProps> = ({ data, onSaved }) => {
  const signalsApi = useSignalsRequest();
  const campaignsApi = useCampaignsRequest();
  const apiRef = useRef({ signalsApi, campaignsApi });
  apiRef.current = { signalsApi, campaignsApi };
  const { store: { user } } = useAuthStore();
  const { setToast } = useAppActions();

  const { settings, canEdit, myCampaign } = data;
  const [icp, setIcp] = useState(settings.icp);
  const [mode, setMode] = useState<SignalsMode>(settings.mode);
  const [autoApprove, setAutoApprove] = useState(String(settings.autoApproveScore));
  const [ignore, setIgnore] = useState(String(settings.ignoreScore));
  const [saving, setSaving] = useState(false);
  const [campaigns, setCampaigns] = useState<ICampaign[] | null>(null);

  useEffect(() => {
    setIcp(settings.icp);
    setMode(settings.mode);
    setAutoApprove(String(settings.autoApproveScore));
    setIgnore(String(settings.ignoreScore));
  }, [settings]);

  useEffect(() => {
    let stale = false;
    apiRef.current.campaignsApi.getCampaigns('signals-settings-campaigns').then(res => {
      if (!stale) setCampaigns(filterEnrollableCampaigns(res?.campaigns ?? [], user?._id));
    });
    return () => { stale = true; };
  }, [user?._id]);

  const dirty = icp.offer !== settings.icp.offer
    || icp.idealBuyer !== settings.icp.idealBuyer
    || icp.notAFit !== settings.icp.notAFit
    || mode !== settings.mode
    || autoApprove !== String(settings.autoApproveScore)
    || ignore !== String(settings.ignoreScore);

  const save = async () => {
    setSaving(true);
    const res = await apiRef.current.signalsApi.updateSettings({
      icp,
      mode,
      autoApproveScore: Number(autoApprove),
      ignoreScore: Number(ignore),
    });
    setSaving(false);
    if (!res?.success) {
      setToast({ type: 'error', message: res?.message || 'Could not save the settings.' });
      return;
    }
    setToast({
      type: 'success',
      message: res.data?.rescoring
        ? 'Saved. Open leads are being re-scored against the new ideal buyer — it takes a few minutes.'
        : 'Settings saved.',
    });
    onSaved();
  };

  const pickCampaign = async (campaignId: string) => {
    const res = await apiRef.current.signalsApi.setMyCampaign(campaignId || null);
    if (!res?.success) {
      setToast({ type: 'error', message: res?.message || 'Could not set your Signals campaign.' });
      return;
    }
    setToast({ type: 'success', message: campaignId ? `Autopilot leads you own will go to “${res.data?.myCampaign?.name}”.` : 'Signals campaign cleared.' });
    onSaved();
  };

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle>Ideal buyer and autopilot</CardTitle>
          <Muted>An AI scores every new person 0–100 against your ideal buyer and says why. Nothing is sent without the rules below.</Muted>
        </div>
        <Pill
          $bg={settings.mode === 'autopilot' ? styles.colors.primary600 : styles.colors.primary100}
          $fg={settings.mode === 'autopilot' ? styles.colors.white : styles.colors.primary600}
        >
          {settings.mode === 'autopilot' ? 'Autopilot on' : 'Human-pilot'}
        </Pill>
      </CardHead>

      <Grid>
        <Column>
          {FIELDS.map(f => (
            <Field key={f.key}>
              <Label htmlFor={`signals-icp-${f.key}`}>{f.label}</Label>
              <TextArea
                id={`signals-icp-${f.key}`}
                rows={f.rows}
                value={icp[f.key]}
                placeholder={f.placeholder}
                disabled={!canEdit}
                onChange={e => setIcp(prev => ({ ...prev, [f.key]: e.target.value }))}
              />
            </Field>
          ))}
          {!canEdit && <Muted>Only workspace admins can change the ideal buyer, the mode and the scores.</Muted>}
        </Column>

        <Column>
          <Field>
            <Label as="span">Mode</Label>
            <Segmented role="radiogroup" aria-label="Mode">
              {(['human', 'autopilot'] as SignalsMode[]).map(m => (
                <SegmentBtn
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={mode === m}
                  $active={mode === m}
                  disabled={!canEdit}
                  onClick={() => setMode(m)}
                >
                  {m === 'human' ? 'Human-pilot' : 'Autopilot'}
                </SegmentBtn>
              ))}
            </Segmented>
            <Muted>
              {mode === 'autopilot'
                ? 'Leads at or above the auto-approve score go straight into their owner’s Signals campaign. The rest wait for a person.'
                : 'Every lead waits for a person to approve or add it.'}
            </Muted>
          </Field>

          {/* Auto-approve and the Signals campaign only act on autopilot; human-pilot shows just the hide score. */}
          <Field>
            <Scores $single={mode !== 'autopilot'}>
              {mode === 'autopilot' && (
                <Field>
                  <Label htmlFor="signals-auto-approve">Auto-approve at</Label>
                  <NumberInput
                    id="signals-auto-approve"
                    type="number"
                    min={1}
                    max={100}
                    value={autoApprove}
                    disabled={!canEdit}
                    onChange={e => setAutoApprove(e.target.value)}
                  />
                </Field>
              )}
              <Field>
                <Label htmlFor="signals-ignore">Hide below</Label>
                <NumberInput
                  id="signals-ignore"
                  type="number"
                  min={0}
                  max={100}
                  value={ignore}
                  disabled={!canEdit}
                  onChange={e => setIgnore(e.target.value)}
                />
              </Field>
            </Scores>
            <Muted>
              {mode === 'autopilot'
                ? 'Fit at or above “Auto-approve at” goes straight into the owner’s Signals campaign. Below “Hide below” goes to the Low fit tab.'
                : 'People the AI scores below this go to the Low fit tab instead of your review list.'}
            </Muted>
          </Field>

          {mode === 'autopilot' && (
            <Field>
              <Label htmlFor="signals-my-campaign">My Signals campaign</Label>
              <Select
                id="signals-my-campaign"
                value={myCampaign?._id || ''}
                onChange={e => pickCampaign(e.target.value)}
                disabled={campaigns === null}
              >
                <option value="">{campaigns === null ? 'Loading…' : 'None — approve instead'}</option>
                {myCampaign && !campaigns?.some(c => c._id === myCampaign._id) && (
                  <option value={myCampaign._id}>{myCampaign.name}</option>
                )}
                {(campaigns ?? []).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Select>
              <Muted>High-fit leads you own (seen first by your LinkedIn account) go here. Without one, they are approved for you to add.</Muted>
            </Field>
          )}
        </Column>
      </Grid>

      {canEdit && (
        <Footer>
          <PrimaryButton type="button" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </Footer>
      )}
    </Card>
  );
};

export default SignalSettingsPanel;

// ─── Styles ───────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 24px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Column = styled.div`
  display: flex; flex-direction: column; gap: 14px;
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 5px;
`;

const Label = styled.label`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: ${styles.colors.black500};
`;

const TextArea = styled.textarea`
  width: 100%; box-sizing: border-box; resize: vertical;
  padding: 9px 12px; border-radius: 8px; outline: none;
  border: 1.5px solid #dde3ee; background: ${styles.colors.white};
  font-family: NunitoSans; font-size: 13px; line-height: 19px; color: ${styles.colors.black600};
  &::placeholder { color: ${styles.colors.black300}; }
  &:focus { border-color: ${styles.colors.primary600}; box-shadow: 0 0 0 3px rgba(89,100,255,0.08); }
  &:disabled { background: ${styles.colors.black100}; }
`;

const Segmented = styled.div`
  display: inline-flex; padding: 3px; gap: 3px; align-self: flex-start;
  border-radius: 10px; background: ${styles.colors.black100};
`;

const SegmentBtn = styled.button<{ $active: boolean }>`
  height: 32px; padding: 0 14px; border-radius: 8px; border: none; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  background: ${({ $active }) => ($active ? styles.colors.white : 'transparent')};
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black400)};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(51,57,81,0.12)' : 'none')};
  &:disabled { cursor: default; }
`;

const Scores = styled.div<{ $single?: boolean }>`
  display: grid; grid-template-columns: ${({ $single }) => ($single ? 'minmax(0, 180px)' : '1fr 1fr')}; gap: 12px;
`;

const NumberInput = styled.input`
  height: 36px; width: 100%; box-sizing: border-box;
  padding: 0 12px; border-radius: 8px; outline: none;
  border: 1.5px solid #dde3ee; background: ${styles.colors.white};
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black600};
  &:focus { border-color: ${styles.colors.primary600}; }
  &:disabled { background: ${styles.colors.black100}; }
`;

const Footer = styled.div`
  display: flex; justify-content: flex-end;
`;
